import { Button, Input, message } from 'antd'
import { convertPdfToPngsConverter } from 'features/fde/PdfToPngConverter'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { useLibraDocument } from 'features/libra/document/LibraDocumentContext'
import { useRolodexSignedURLs } from 'features/rolodex'
import { Check, X } from 'lucide-react'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { GeckoEntry, RolodexEntryType } from 'types'
import { v4 } from 'uuid'

interface CropDocumentPanelProps {
  sourceDocument: GeckoEntry
  unitId: string
  onCropComplete: (referenceImageId: string) => void
  onCancel: () => void
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px;
  background-color: ${({ theme }) => theme.forecast.background2};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.forecast.border};
`

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const Subtitle = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const Actions = styled.div`
  display: flex;
  gap: 8px;
`

const FormSection = styled.div`
  padding: 16px;
  background-color: ${({ theme }) => theme.forecast.background2};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.forecast.border};
`

const Label = styled.label`
  display: block;
  font-weight: 500;
  font-size: 13px;
  color: ${({ theme }) => theme.forecast.defaultText};
  margin-bottom: 8px;
`

const Hint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.forecast.secondaryText};
  margin-top: 4px;
`

export const CropDocumentPanel: FC<CropDocumentPanelProps> = ({
  sourceDocument,
  unitId,
  onCropComplete,
  onCancel,
}) => {
  const { rolodexPut, rolodexUploadFile } = useRolodexEdit()
  const { setCropMode, currentViewPage } = useLibraDocument()

  // Auto-generate a default crop name from the source document name
  const autoGeneratedName = useMemo(() => {
    const data = sourceDocument.data as {
      visible_name?: string
      filename?: string
    }
    const baseName = data.visible_name || data.filename || 'document'
    // Strip file extension if present
    const nameWithoutExt = baseName.replace(/\.[^/.]+$/, '')
    const salt = v4().substring(0, 6)
    return `${nameWithoutExt}-cropped-section-${salt}`
  }, [sourceDocument])

  const [cropName, setCropName] = useState(autoGeneratedName)
  const [isSaving, setIsSaving] = useState(false)
  const [currentPage, setCurrentPage] = useState(currentViewPage)
  const [cropRect, setCropRect] = useState<{
    x: number
    y: number
    width: number
    height: number
  }>()

  useEffect(() => {
    setCropMode({
      enabled: true,
      sourceDocumentId: sourceDocument.id,
      cropRect: cropRect,
      currentPage: currentPage,
      onCropComplete: (rect) => {
        setCropRect(rect)
      },
      onPageChange: (page: number) => {
        setCurrentPage(page)
        // Clear crop when page changes
        setCropRect(undefined)
      },
    })

    return () => {
      setCropMode({ enabled: false })
    }
  }, [sourceDocument.id, setCropMode, cropRect, currentPage])

  // Get filename from the document entry
  const sourceDocumentFilename = useMemo(() => {
    const data = sourceDocument.data as {
      filename?: string
      fields?: { filename?: string }
    }
    return data.filename || data.fields?.filename
  }, [sourceDocument])

  const { urls: signedUrls } = useRolodexSignedURLs(
    useMemo(
      () =>
        sourceDocumentFilename
          ? [{ entry_id: sourceDocument.id, path: sourceDocumentFilename }]
          : undefined,
      [sourceDocument.id, sourceDocumentFilename]
    )
  )

  const signedUrl = signedUrls.length > 0 ? signedUrls[0] : undefined

  const handleSaveCrop = useCallback(async () => {
    if (!cropName.trim()) {
      message.error('Please enter a name for this zone reference image')
      return
    }

    if (!cropRect) {
      message.error('Please draw a crop rectangle on the image')
      return
    }

    if (!signedUrl) {
      message.error('Document not loaded')
      return
    }

    setIsSaving(true)

    try {
      const isPdf = sourceDocumentFilename?.toLowerCase().endsWith('.pdf')

      const pageRotations = (
        sourceDocument.data as { pageRotations?: Record<string, number> }
      ).pageRotations
      const rotation = pageRotations?.[String(currentPage)] ?? 0

      let bitmapToCrop: ImageBitmap

      if (isPdf) {
        const pngDataUrls = await convertPdfToPngsConverter(signedUrl, {
          scale: 2,
          pageRotations,
        })

        if (pngDataUrls.length === 0) {
          throw new Error('Failed to convert PDF to image')
        }

        const pageIndex = currentPage - 1
        if (pageIndex >= pngDataUrls.length) {
          throw new Error(`Page ${currentPage} not found in PDF`)
        }

        const response = await fetch(pngDataUrls[pageIndex])
        const blob = await response.blob()
        bitmapToCrop = await createImageBitmap(blob)
      } else {
        const response = await fetch(signedUrl)
        const blob = await response.blob()
        const rawBitmap = await createImageBitmap(blob)

        if (rotation !== 0) {
          const rad = (rotation * Math.PI) / 180
          const rotCanvas = document.createElement('canvas')
          if (rotation === 90 || rotation === 270) {
            rotCanvas.width = rawBitmap.height
            rotCanvas.height = rawBitmap.width
          } else {
            rotCanvas.width = rawBitmap.width
            rotCanvas.height = rawBitmap.height
          }
          const rotCtx = rotCanvas.getContext('2d')
          if (!rotCtx)
            throw new Error('Failed to get canvas context for rotation')
          rotCtx.translate(rotCanvas.width / 2, rotCanvas.height / 2)
          rotCtx.rotate(rad)
          rotCtx.drawImage(
            rawBitmap,
            -rawBitmap.width / 2,
            -rawBitmap.height / 2
          )
          bitmapToCrop = await createImageBitmap(rotCanvas)
        } else {
          bitmapToCrop = rawBitmap
        }
      }

      const canvas = document.createElement('canvas')
      canvas.width = cropRect.width
      canvas.height = cropRect.height
      const ctx = canvas.getContext('2d')
      if (!ctx) throw new Error('Failed to get canvas context')

      // Draw the cropped region from the (possibly rotated) bitmap
      ctx.drawImage(
        bitmapToCrop,
        cropRect.x,
        cropRect.y,
        cropRect.width,
        cropRect.height,
        0,
        0,
        cropRect.width,
        cropRect.height
      )

      const pngBlob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (blob) =>
            blob ? resolve(blob) : reject(new Error('Failed to create blob')),
          'image/png',
          0.95
        )
      })

      // Create REPORT entry for the zone reference image
      const newEntryId = v4()
      const filename = `${cropName.toLowerCase().replace(/\s+/g, '-')}-${v4().substring(0, 6)}.png`

      const entryData = {
        id: newEntryId,
        type: RolodexEntryType.REPORT,
        tags: { ...sourceDocument.raw.tags, unit: unitId },
        data: {
          visible_name: cropName,
          categories: ['Zone Reference Images'],
          filename: filename,
          file_type: 'png', // CRITICAL: This tells DocumentPreview it's an image
          fields: {
            source_drawing_id: sourceDocument.id,
            source_drawing_name:
              (sourceDocument.data as { visible_name?: string }).visible_name ||
              sourceDocumentFilename,
            crop_region: cropRect,
            page_number: currentPage,
          },
        },
      }

      await rolodexPut([entryData])
      await rolodexUploadFile(newEntryId, pngBlob, filename)

      message.success('Zone reference image created successfully')
      onCropComplete(newEntryId)
    } catch (error) {
      console.error('Error saving crop:', error)
      message.error('Failed to save zone reference image')
    } finally {
      setIsSaving(false)
    }
  }, [
    cropName,
    cropRect,
    currentPage,
    signedUrl,
    sourceDocument,
    sourceDocumentFilename,
    unitId,
    rolodexPut,
    rolodexUploadFile,
    onCropComplete,
  ])

  const sourceDocumentName = useMemo(() => {
    const data = sourceDocument.data as {
      visible_name?: string
      filename?: string
    }
    return (
      data.visible_name ||
      data.filename ||
      `Document ${sourceDocument.id.substring(0, 8)}`
    )
  }, [sourceDocument])

  return (
    <Container>
      <Header>
        <HeaderContent>
          <Title>Create Zone Reference Image</Title>
          <Subtitle>Cropping from: {sourceDocumentName}</Subtitle>
        </HeaderContent>
        <Actions>
          <Button icon={<X size={16} />} onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            type='primary'
            icon={<Check size={16} />}
            onClick={handleSaveCrop}
            loading={isSaving}
            disabled={!cropName.trim() || !cropRect}>
            Save Cropped Region
          </Button>
        </Actions>
      </Header>

      <FormSection>
        <Label>Reference Image Name</Label>
        <Input
          value={cropName}
          onChange={(e) => setCropName(e.target.value)}
          placeholder='e.g., "Front Elevation", "Side View A"'
          disabled={isSaving}
        />
        <Hint>Give this cropped region a descriptive name</Hint>
      </FormSection>
    </Container>
  )
}
